import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { getRef } from '../lib/referral';
import { trackEvent } from '../lib/analytics';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Button } from './ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from '../hooks/use-toast';
import { Phone, Mail, MessageCircle, Loader2 } from 'lucide-react';
import { useT } from '../i18n';
import FormSuccess from './FormSuccess';
import { HumanCheck } from './HumanCheck';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const emptyForm = {
  firstName: '',
  lastName: '',
  email: '',
  restaurant: '',
  interest: '',
  phone: '',
  notes: '',
};

export default function AuditForm() {
  const { t, lang } = useT();
  const [form, setForm] = useState(emptyForm);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [humanToken, setHumanToken] = useState('');
  const nav = useNavigate();

  const interests = t('audit.interests');
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    const name = sessionStorage.getItem('kx_restaurant_name');
    if (name) {
      setForm((f) => ({ ...f, restaurant: name }));
      sessionStorage.removeItem('kx_restaurant_name');
    }
    const onName = (e) => {
      setForm((f) => ({ ...f, restaurant: e.detail }));
      sessionStorage.removeItem('kx_restaurant_name');
    };
    window.addEventListener('kx:restaurant-name', onName);
    return () => window.removeEventListener('kx:restaurant-name', onName);
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.firstName || !form.email) {
      toast({
        title: t('audit.missingTitle'),
        description: t('audit.missingBody'),
      });
      return;
    }
    if (!humanToken) {
      toast({
        title: lang === 'es' ? 'Verificación pendiente' : 'Verification pending',
        description: lang === 'es' ? 'Marca "No soy un robot" para enviar.' : 'Check "I\'m not a robot" to send.',
      });
      return;
    }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/lead`, {
        source: 'audit',
        first_name: form.firstName,
        last_name: form.lastName,
        email: form.email,
        company: form.restaurant,
        interest: form.interest,
        phone: form.phone,
        notes: form.notes,
        locale: lang,
        source_page: typeof window !== 'undefined' ? window.location.pathname : '',
        metadata: getRef() ? { ref: getRef() } : {},
      }, { headers: { 'X-Human-Token': humanToken } });
      trackEvent('contact_form_submitted', { source: 'audit' });
      trackEvent('consultation_requested', { source: 'audit' });
      trackEvent('book_strategy_call', { source: 'audit' });
      trackEvent('free_audit_start', { source: 'audit_form' });
      const fullName = `${form.firstName} ${form.lastName}`.trim();
      if (fullName) localStorage.setItem('kx_welcome_name', fullName);
      if (form.email) localStorage.setItem('kx_welcome_email', form.email.trim());
      if (form.restaurant) localStorage.setItem('kx_welcome_restaurant', form.restaurant.trim());
      setSubmitted(true);
      if (r.data?.already_known) {
        toast({
          title: lang === 'es' ? 'Ya nos conocemos' : 'We already know each other',
          description: lang === 'es' ? 'Tenemos tus datos guardados — no hace falta repetirlos. Te llevamos a la demo.' : 'We already have your details saved — no need to repeat them. Taking you to the demo.',
        });
      } else {
        toast({
          title: t('audit.successTitle'),
          description: t('audit.successBody'),
        });
      }
      setTimeout(() => nav('/demo'), 2200);
    } catch (err) {
      console.error('Audit submission failed', err);
      toast({
        title: t('audit.errorTitle'),
        description: t('audit.errorBody'),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="audit" className="relative py-20 lg:py-28 overflow-hidden bg-gradient-to-br from-[#F77F11] via-[#ff8a24] to-[#ff9b4a]">
      {/* layered subtle texture */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.10] pointer-events-none"
        style={{
          backgroundImage:
            'radial-gradient(circle at 22% 28%, #fff 1px, transparent 1.6px), radial-gradient(circle at 78% 72%, #fff 1px, transparent 1.6px)',
          backgroundSize: '120px 120px, 170px 170px',
        }}
      />
      <div
        aria-hidden="true"
        className="absolute -top-32 -right-32 w-[460px] h-[460px] rounded-full opacity-30 pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.45), transparent 65%)' }}
      />
      <div
        aria-hidden="true"
        className="absolute -bottom-40 -left-32 w-[520px] h-[520px] rounded-full opacity-25 pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.35), transparent 70%)' }}
      />

      <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12">
        <div className="text-white">
          <p className="text-[11px] tracking-[0.22em] font-semibold uppercase mb-4 opacity-90">
            {t('audit.eyebrow')}
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-[44px] font-semibold leading-[1.08] tracking-[-0.02em]">
            {t('audit.titleA')}{' '}
            <span className="underline decoration-white/35 underline-offset-[6px]">
              {t('audit.titleAccent')}
            </span>
          </h2>
          <p className="mt-5 text-white/90 leading-relaxed max-w-lg text-[15.5px]">{t('audit.body')}</p>
          <p className="mt-4 text-white italic text-[14.5px] max-w-lg leading-relaxed">
            {lang === 'es'
              ? 'La pregunta ya no es si la IA cambiará la restauración. Es quién la tendrá trabajando a su favor primero.'
              : "The question is no longer whether AI will change restaurants. It's who will have it working in their favor first."}
          </p>

          {/* Editorial human-touch photo — landscape ratio matched to the column,
              warm tones, subtle hover zoom. Curated chef-at-the-pass moment. */}
          <div className="mt-8 relative group rounded-2xl overflow-hidden shadow-[0_30px_70px_-30px_rgba(15,17,23,0.55)] max-w-lg">
            <div className="aspect-[16/9] overflow-hidden bg-neutral-900">
              <img
                src="https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&w=1200&q=80"
                alt={lang === 'es' ? 'Cocina activa de un restaurante moderno' : 'Active kitchen of a modern restaurant'}
                loading="lazy"
                className="w-full h-full object-cover scale-[1.02] group-hover:scale-[1.05] transition-transform duration-[500ms] ease-out"
              />
            </div>
            {/* Warmth + readability gradient (does not dominate) */}
            <div
              aria-hidden="true"
              className="absolute inset-0 bg-gradient-to-tr from-[#0F1117]/45 via-transparent to-[#F77F11]/15 pointer-events-none"
            />
            <div className="absolute bottom-0 left-0 right-0 p-5">
              <p className="text-white/90 text-[11px] tracking-[0.22em] font-semibold uppercase">
                {lang === 'es' ? 'Hospitalidad' : 'Hospitality'}
              </p>
              <p className="mt-1 text-white text-[15px] font-semibold tracking-[-0.005em] leading-snug max-w-sm">
                {lang === 'es'
                  ? 'Te ayudamos a recuperar las horas que tu equipo le debe a tus clientes.'
                  : 'We give your team back the hours they owe to your guests.'}
              </p>
            </div>
          </div>

          <div className="mt-8 space-y-3">
            <a
              href="https://wa.me/14085901102"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-3"
              style={{ WebkitTapHighlightColor: 'transparent' }}
              data-testid="audit-cta-whatsapp"
            >
              <div className="w-11 h-11 rounded-full bg-white/15 group-hover:bg-[#25D366] flex items-center justify-center transition-colors shrink-0">
                <MessageCircle className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-[0.18em] opacity-90 font-semibold">
                  {lang === 'es' ? 'WhatsApp Business (ENG | ESP)' : 'WhatsApp Business (ENG | ESP)'}
                </p>
                <p className="font-semibold text-[15px]">+1 (408) 590-1102</p>
              </div>
            </a>
            <a href="tel:+14155982310" className="group flex items-center gap-3" style={{ WebkitTapHighlightColor: 'transparent' }} data-testid="audit-cta-phone">
              <div className="w-11 h-11 rounded-full bg-white/15 group-hover:bg-white/35 flex items-center justify-center transition-colors shrink-0">
                <Phone className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-[0.18em] opacity-90 font-semibold">
                  {lang === 'es' ? 'Teléfono Comercial (ENG)' : 'Business Phone (ENG)'}
                </p>
                <p className="font-semibold text-[15px]">+1 (415) 598-2310</p>
              </div>
            </a>
            <a href="mailto:hello@kitxens.com" className="group flex items-center gap-3" style={{ WebkitTapHighlightColor: 'transparent' }} data-testid="audit-cta-email">
              <div className="w-11 h-11 rounded-full bg-white/15 group-hover:bg-white/35 flex items-center justify-center transition-colors shrink-0">
                <Mail className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-[0.18em] opacity-90 font-semibold">{t('audit.emailInquiry')}</p>
                <p className="font-semibold text-[15px]">hello@kitxens.com</p>
              </div>
            </a>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-[0_40px_100px_-30px_rgba(0,0,0,0.35)] p-7 sm:p-8">
          {submitted ? (
            <FormSuccess lang={lang} testId="audit-form-success" />
          ) : (
            <form onSubmit={onSubmit} className="space-y-4" data-testid="audit-form">
              <p className="text-xs tracking-[0.25em] font-semibold text-[#F77F11] mb-2">
                {t('audit.formEyebrow')}
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="fn">{t('audit.firstName')}*</Label>
                  <Input
                    id="fn"
                    value={form.firstName}
                    onChange={(e) => update('firstName', e.target.value)}
                    className="mt-1"
                    autoComplete="given-name"
                    data-testid="audit-form-firstname"
                  />
                </div>
                <div>
                  <Label htmlFor="ln">{t('audit.lastName')}</Label>
                  <Input
                    id="ln"
                    value={form.lastName}
                    onChange={(e) => update('lastName', e.target.value)}
                    className="mt-1"
                    autoComplete="family-name"
                    data-testid="audit-form-lastname"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="em">{t('audit.workEmail')}*</Label>
                <Input
                  id="em"
                  type="email"
                  value={form.email}
                  onChange={(e) => update('email', e.target.value)}
                  className="mt-1"
                  autoComplete="email"
                  data-testid="audit-form-email"
                />
              </div>
              <div>
                <Label htmlFor="rs">{t('audit.restaurant')}</Label>
                <Input
                  id="rs"
                  value={form.restaurant}
                  onChange={(e) => update('restaurant', e.target.value)}
                  className="mt-1"
                  data-testid="audit-form-restaurant"
                />
              </div>
              <div>
                <Label>{t('audit.interested')}</Label>
                <Select value={form.interest} onValueChange={(v) => update('interest', v)}>
                  <SelectTrigger className="mt-1" data-testid="audit-form-interest">
                    <SelectValue placeholder={t('audit.selectSolution')} />
                  </SelectTrigger>
                  <SelectContent>
                    {interests.map((i) => (
                      <SelectItem key={i} value={i}>
                        {i}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="ph">{t('audit.phone')}</Label>
                <Input
                  id="ph"
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={(e) => update('phone', e.target.value)}
                  className="mt-1"
                  autoComplete="tel"
                  data-testid="audit-form-phone"
                />
              </div>
              <div>
                <Label htmlFor="nt">{t('audit.additional')}</Label>
                <Textarea
                  id="nt"
                  rows={3}
                  value={form.notes}
                  onChange={(e) => update('notes', e.target.value)}
                  className="mt-1"
                  data-testid="audit-form-notes"
                />
              </div>
              <HumanCheck onToken={setHumanToken} className="mb-3" />
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#F77F11] hover:bg-[#d8651a] text-white h-12 text-[15px] font-semibold"
                data-testid="audit-form-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> ...
                  </>
                ) : (
                  t('audit.submit')
                )}
              </Button>
              <p className="text-xs text-neutral-500 text-center">{t('audit.disclaimer')}</p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
